<template>
    <transitional name="fade" mode="out-in">
        <call-to-action v-if="!canBack" :disabled="!canComplete" @clicked="complete">
            <slot name="continue" />
            <template v-if="countdown > 0 && !canComplete">
                -
                <span class="remaining-time">{{ secondsToTime(countdown) }}</span>
            </template>
        </call-to-action>

        <call-to-action v-else class="link" @clicked="back">
            <slot name="back" />
        </call-to-action>
    </transitional>
</template>

<script lang="ts" setup>
import { CallToAction } from '@btcdirect/components';
import Transitional from '@btcdirect/shared/src/components/transition/transitional.vue';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { useInterval } from '@btcdirect/shared/src/composables/util/interval';
import { secondsToTime } from '@btcdirect/shared/src/composables/util/time';
import { useTimersStore } from '@btcdirect/stores';
import { computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps<{ interval: number; maximumRuntime: number; isRunning: boolean; enableCompleteAction: boolean }>();

const emit = defineEmits([EmitEvents.COMPLETED, EmitEvents.BACK, EmitEvents.STOPPED, EmitEvents.POLLED]);

const { startSingleCountdown, stop } = useInterval();

const TIMER = 'polling-call-to-action';

const canComplete = computed(() => !props.isRunning && props.enableCompleteAction);

const complete = () => {
    if (!canComplete.value) {
        return;
    }

    emit(EmitEvents.COMPLETED);
};

const canBack = computed(() => !props.isRunning && !props.enableCompleteAction);

const back = () => {
    if (!canBack.value) {
        return;
    }

    emit(EmitEvents.BACK);
};

const timersStore = useTimersStore();
const countdown = computed<number>(() => timersStore.countdowns.get(TIMER));

const startPolling = () => {
    startSingleCountdown(TIMER, props.maximumRuntime, props.interval, async () => {
        if (countdown.value % (props.interval / 1000) === 0) {
            emit(EmitEvents.POLLED);
        }

        if (countdown.value < 1) {
            emit(EmitEvents.STOPPED);
        }
    });
};

const stopPolling = () => {
    stop(TIMER);
};

const configure = (): void => {
    if (false === props.isRunning) {
        stopPolling();
        return;
    }

    startPolling();
};

watch(() => props.isRunning, configure);

onMounted(configure);
onUnmounted(stopPolling);
</script>
